import React, { useEffect, useState } from "react";
import { addEventTypeQuestion, deleteEventType, listCustomEventTypes, updateEventType } from "../../Services/DynamicEventTypes";
import { generateSmartPrefill, testGeminiKey } from "../../Services/Gemini";
import { fetchWebImageForEventType } from "../../Services/WebImage";
import Loader from "../../Layouts/Loader";
import EventTypeForm from "./Components/EventTypeForm";
import QuestionsManager from "./Components/QuestionsManager";
import SmartPrefillModal from "./Components/SmartPrefillModal";
import { SMART_PREFILL_CONFIG } from "./constants";
import {
    DYNAMIC_EVENT_TYPE_STRINGS,
    getSmartPrefillErrorMessage,
    getSmartPrefillTestErrorMessage,
    SMART_PREFILL_STRINGS
} from "./strings";
import "./DynamicEventTypes.css";

const DynamicEventTypes = () => {
    const [eventTypes, setEventTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedEventType, setSelectedEventType] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [showQuestionsManager, setShowQuestionsManager] = useState(false);
    const [showSmartPrefill, setShowSmartPrefill] = useState(false);
    const [smartPrefillLoading, setSmartPrefillLoading] = useState(false);
    const [smartPrefillError, setSmartPrefillError] = useState(null);
    const [prefillInitialValues, setPrefillInitialValues] = useState(null);
    const [prefillQuestions, setPrefillQuestions] = useState([]);
    const [prefillImage, setPrefillImage] = useState(null);
    const [prefillImageLoading, setPrefillImageLoading] = useState(false);
    const [prefillImageError, setPrefillImageError] = useState(null);
    const [keyTestLoading, setKeyTestLoading] = useState(false);
    const [keyTestResult, setKeyTestResult] = useState(null);

    const resetPrefillState = () => {
        if (prefillImage?.isObjectUrl && prefillImage?.previewUrl) {
            URL.revokeObjectURL(prefillImage.previewUrl);
        }
        setPrefillInitialValues(null);
        setPrefillQuestions([]);
        setPrefillImage(null);
        setPrefillImageLoading(false);
        setPrefillImageError(null);
    };

    const reload = () => {
        setLoading(true);
        setError(null);
        listCustomEventTypes()
            .then(res => {
                setEventTypes(res.data.data.responseData || []);
            })
            .catch((err) => {
                console.error('Error loading event types:', err);
                setError("Failed to load event types");
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        reload();
    }, []);

    const handleCreate = () => {
        resetPrefillState();
        setSelectedEventType(null);
        setShowForm(true);
    };

    const handleEdit = (eventType) => {
        resetPrefillState();
        setSelectedEventType(eventType);
        setShowForm(true);
    };

    const handleManageQuestions = (eventType) => {
        setSelectedEventType(eventType);
        setShowQuestionsManager(true);
    };

    const handleDelete = (eventType) => {
        if (window.confirm(`Are you sure you want to delete "${eventType.display_name}"?`)) {
            deleteEventType(eventType.id)
                .then(() => {
                    reload();
                })
                .catch((err) => {
                    console.error('Error deleting event type:', err);
                    setError("Failed to delete event type");
                });
        }
    };

    const handleToggleStatus = (eventType) => {
        const nextStatus = !eventType.is_active;
        updateEventType(eventType.id, { is_active: nextStatus })
            .then(() => {
                reload();
            })
            .catch((err) => {
                console.error('Error updating event type status:', err);
                setError(DYNAMIC_EVENT_TYPE_STRINGS.statusUpdateError);
            });
    };

    const handleFormClose = (saved) => {
        setShowForm(false);
        setSelectedEventType(null);
        resetPrefillState();
        if (saved?.warning) {
            setError(saved.warning);
        }
        if (saved?.saved || saved === true) {
            reload();
        }
    };

    const handleQuestionsClose = () => {
        setShowQuestionsManager(false);
        setSelectedEventType(null);
    };

    const handleSmartPrefillOpen = () => {
        setSmartPrefillError(null);
        setKeyTestResult(null);
        setShowSmartPrefill(true);
    };

    const handleSmartPrefillClose = () => {
        setShowSmartPrefill(false);
        setSmartPrefillError(null);
        setKeyTestResult(null);
    };

    const handleCreateQuestions = async (eventTypeId) => {
        if (!Array.isArray(prefillQuestions) || prefillQuestions.length === 0) {
            return;
        }
        for (let index = 0; index < prefillQuestions.length; index += 1) {
            const question = prefillQuestions[index];
            if (!question?.question || !Array.isArray(question.choices)) {
                continue;
            }
            const payload = {
                question: question.question,
                choices: question.choices,
                sort_order: Number.isFinite(question.sort_order) ? question.sort_order : index,
                category_id: SMART_PREFILL_CONFIG.defaultCategoryId
            };
            await addEventTypeQuestion(eventTypeId, payload);
        }
    };

    const handleSmartPrefillGenerate = async (prompt, apiKey) => {
        setSmartPrefillLoading(true);
        setSmartPrefillError(null);
        setPrefillImageError(null);
        try {
            const result = await generateSmartPrefill(prompt, apiKey);
            setPrefillInitialValues(result.eventType);
            setPrefillQuestions(result.questions);
            setSelectedEventType(null);
            setShowForm(true);
            setShowSmartPrefill(false);
            setPrefillImageLoading(true);
            fetchWebImageForEventType({ displayName: result.eventType.display_name, prompt })
                .then((imageResult) => {
                    setPrefillImage(imageResult);
                })
                .catch(() => {
                    setPrefillImageError(DYNAMIC_EVENT_TYPE_STRINGS.webImageError);
                })
                .finally(() => {
                    setPrefillImageLoading(false);
                });
        } catch (prefillError) {
            setSmartPrefillError(getSmartPrefillErrorMessage(prefillError));
        } finally {
            setSmartPrefillLoading(false);
        }
    };

    const handleTestKey = async (apiKey) => {
        setKeyTestLoading(true);
        setKeyTestResult(null);
        try {
            const result = await testGeminiKey(apiKey);
            setKeyTestResult({ ok: true, ...result });
        } catch (testError) {
            setKeyTestResult({
                ok: false,
                message: getSmartPrefillTestErrorMessage(testError),
                status: testError?.status
            });
        } finally {
            setKeyTestLoading(false);
        }
    };

    return (
        <>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="page_header">
                    <h2 className="mb-0">Dynamic 5on Events</h2>
                    <p className="text-muted mb-0">Manage custom event types for Five On app (types 9+)</p>
                </div>
                <div className="d-flex gap-2">
                    <button className="btn btn-primary" onClick={handleCreate}>
                        + Create Event Type
                    </button>
                    <button className="btn btn-outline-primary" onClick={handleSmartPrefillOpen}>
                        {SMART_PREFILL_STRINGS.buttonLabel}
                    </button>
                </div>
            </div>

            {error && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                    {error}
                    <button type="button" className="btn-close" onClick={() => setError(null)}></button>
                </div>
            )}

            <div className="card">
                <div className="card-body">
                    {loading ? (
                        <Loader />
                    ) : eventTypes.length === 0 ? (
                        <div className="text-center py-5">
                            <p className="text-muted mb-3">No custom event types yet</p>
                            <p className="text-muted small">Custom event types are only available in the Five On app</p>
                            <button className="btn btn-primary mt-2" onClick={handleCreate}>
                                Create First Event Type
                            </button>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-striped table-hover mb-0">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Name</th>
                                        <th>Display Name</th>
                                        <th>Emoji</th>
                                        <th>Color</th>
                                        <th>Event Date</th>
                                        <th>Placeholder Image</th>
                                        <th>Guests Range</th>
                                        <th>Questions</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {eventTypes.map((eventType) => (
                                        <tr key={eventType.id}>
                                            <td>{eventType.id}</td>
                                            <td>{eventType.name}</td>
                                            <td>
                                                <span className="badge" style={{ backgroundColor: eventType.variant_color, color: 'white' }}>
                                                    {eventType.display_name}
                                                </span>
                                            </td>
                                            <td style={{ fontSize: '1.5rem' }}>{eventType.emoji}</td>
                                            <td>
                                                <div className="d-flex align-items-center gap-2">
                                                    <div
                                                        style={{
                                                            width: '30px',
                                                            height: '30px',
                                                            backgroundColor: eventType.variant_color,
                                                            borderRadius: '4px',
                                                            border: '1px solid #dee2e6'
                                                        }}
                                                    ></div>
                                                    <code>{eventType.variant_color}</code>
                                                </div>
                                            </td>
                                            <td>{eventType.event_date || "—"}</td>
                                            <td>
                                                {eventType.placeholder_image_url ? (
                                                    <img 
                                                        src={eventType.placeholder_image_url} 
                                                        alt="Placeholder" 
                                                        style={{ 
                                                            width: '60px', 
                                                            height: '60px', 
                                                            objectFit: 'cover', 
                                                            borderRadius: '4px',
                                                            border: '1px solid #dee2e6'
                                                        }}
                                                    />
                                                ) : (
                                                    <span className="text-muted small">No image</span>
                                                )}
                                            </td>
                                            <td>
                                                {eventType.min_estimated_guests} - {eventType.max_estimated_guests}
                                                <small className="text-muted d-block">(default: {eventType.default_estimated_guests})</small>
                                            </td>
                                            <td>
                                                <button
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={() => handleManageQuestions(eventType)}
                                                >
                                                    {eventType.question_count || 0} Questions
                                                </button>
                                            </td>
                                            <td>
                                                <span className={`badge ${eventType.is_active ? 'bg-success' : 'bg-secondary'}`}>
                                                    {eventType.is_active ? DYNAMIC_EVENT_TYPE_STRINGS.statusActive : DYNAMIC_EVENT_TYPE_STRINGS.statusInactive}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="btn-group btn-group-sm">
                                                    <button className="btn btn-outline-primary" onClick={() => handleEdit(eventType)}>
                                                        Edit
                                                    </button>
                                                    <button className="btn btn-outline-secondary" onClick={() => handleToggleStatus(eventType)}>
                                                        {eventType.is_active ? DYNAMIC_EVENT_TYPE_STRINGS.disableButton : DYNAMIC_EVENT_TYPE_STRINGS.enableButton}
                                                    </button>
                                                    <button className="btn btn-outline-danger" onClick={() => handleDelete(eventType)}>
                                                        Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {showForm && (
                <EventTypeForm
                    eventType={selectedEventType}
                    initialValues={prefillInitialValues}
                    prefillQuestions={prefillQuestions}
                    prefillImage={prefillImage}
                    prefillImageLoading={prefillImageLoading}
                    prefillImageError={prefillImageError}
                    onCreateQuestions={handleCreateQuestions}
                    onClose={handleFormClose}
                />
            )}

            {showQuestionsManager && selectedEventType && (
                <QuestionsManager
                    eventType={selectedEventType}
                    onClose={handleQuestionsClose}
                />
            )}
            <SmartPrefillModal
                isOpen={showSmartPrefill}
                onClose={handleSmartPrefillClose}
                onGenerate={handleSmartPrefillGenerate}
                onTestKey={handleTestKey}
                loading={smartPrefillLoading}
                error={smartPrefillError}
                testResult={keyTestResult}
                testLoading={keyTestLoading}
            />
        </>
    );
};

export default DynamicEventTypes;
